// src/layouts/MainLayout.jsx

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { logout } from '../features/auth/authSlice';
import logo from '../assets/logo.svg';
import { paths } from '../routes/helpers';
import GeoNavSection from '../components/GeoNavSection';

function MainLayout({ children }) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate(paths.public.login());
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    return (
        <div className="flex h-screen overflow-hidden">
            {/** MOBILE SIDEBAR OVERLAY */}
            {sidebarOpen && (
                <div 
                    className="md:hidden fixed inset-0 bg-gray-600 bg-opacity-75 z-20"
                    onClick={toggleSidebar}
                ></div>
            )}

            {/** SIDEBAR */}
            <aside 
                className={`${
                    sidebarOpen ? 'translate-x-0' : '-translate-x-full'
                } md:translate-x-0 fixed md:static inset-y-0 left-0 z-30 w-64 bg-gray-900 text-white flex-shrink-0 flex flex-col transition-transform duration-300 ease-in-out`}
            >
                <div className="p-4 flex items-center justify-between border-b border-gray-700">
                    {/* Logo */}
                    <Link to={paths.private.business.list()}>
                        <img src={logo} alt="Oystr" className="h-8 w-auto" />
                    </Link>
                    {/* Close button - only on mobile */}
                    <button 
                        className="md:hidden text-white hover:text-gray-300"
                        onClick={toggleSidebar}
                    >
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                    <Link
                        to={paths.private.dashboardHome()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Dashboard
                    </Link>
                    <Link
                        to={paths.private.business.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Businesses
                    </Link>
                    <Link
                        to={paths.private.booking.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Bookings
                    </Link>
                    <Link
                        to={paths.private.amenity.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Amenities
                    </Link>
                    <Link
                        to={paths.private.barPreference.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Bar Preferences
                    </Link>
                    <Link
                        to={paths.private.category.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Category
                    </Link>
                    <Link
                        to={paths.private.activity.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Activities
                    </Link>
                    <Link
                        to={paths.private.cuisine.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Cuisine
                    </Link>
                    <Link
                        to={paths.private.trend.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Trends
                    </Link>
                    <Link
                        to={paths.private.dietaryPreference.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition text-sm"
                    >
                        Dietary Preferences
                    </Link>
                    <Link
                        to={paths.private.occasion.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Occasions
                    </Link>
                    <Link
                        to={paths.private.item.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Items
                    </Link>
                    <Link
                        to={paths.private.user.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Users
                    </Link>
                    <Link
                        to={paths.private.post.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Posts
                    </Link>
                    <Link
                        to={paths.private.thread.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Threads
                    </Link>
                    <Link
                        to={paths.private.keywordCategory.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Keyword Categories
                    </Link>
                    <Link
                        to={paths.private.businessKeywordsCategories.list()}
                        className="block px-2 py-1 rounded hover:bg-gray-800 transition"
                    >
                        Business Keyword Categories
                    </Link>
                    <GeoNavSection />
                </nav>
            </aside>

            {/** MAIN AREA */}
            <div className="flex flex-col flex-1 w-full overflow-hidden">
                {/** TOP BAR */}
                <header className="bg-white shadow p-2 sm:p-4 flex justify-between items-center">
                    <div className="flex items-center">
                        {/* Mobile menu button */}
                        <button
                            onClick={toggleSidebar}
                            className="md:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 focus:outline-none"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </button>
                        <div className="ml-2 md:ml-0 flex items-center">
                            <img src={logo} alt="Logo" className="h-8 w-auto mr-2" />
                        </div>
                    </div>
                    <div>
                        {/* Right side: could be a profile pic, username, logout button, etc. */}
                        <button 
                            onClick={handleLogout}
                            className="text-gray-700 hover:text-gray-900 transition px-2 py-1"
                        >
                            Logout
                        </button>
                    </div>
                </header>

                {/** CONTENT AREA */}
                <main className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50">
                    {children}
                </main>
            </div>
        </div>
    );
}

export default MainLayout;
